/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { createQueryOptions, createMutationFn } from '@shared/lib/api';
import { OrganizationSettings } from '@shared/schema/organizationSettings';
import {
  OrganizationSettingsData,
  VelocitySettings,
  RepositorySettings,
  AISettings,
  AccessControlSettings,
  OrganizationIntegrationSettings,
  DSAOrgSettings,
  UpdateOrganizationSettingsCategoryRequest,
} from '@shared/types/organizationSettings';

// Get organization settings
export const getOrganizationSettings = (organizationId?: string) => 
  createQueryOptions<OrganizationSettings>(
    organizationId 
      ? `/api/settings/organization?organizationId=${organizationId}` 
      : '/api/settings/organization'
  );

// Get velocity-specific settings
export const getVelocitySettings = (organizationId?: string) =>
  createQueryOptions<VelocitySettings>(
    organizationId
      ? `/api/settings/organization/velocity?organizationId=${organizationId}`
      : '/api/settings/organization/velocity'
  );

// Update full settings
export const updateOrganizationSettings = (organizationId?: string) =>
  createMutationFn<OrganizationSettings, Partial<OrganizationSettingsData>>(
    organizationId
      ? `/api/settings/organization?organizationId=${organizationId}`
      : '/api/settings/organization',
    'PUT'
  );

// Update specific settings category
export const updateOrganizationSettingsCategory = (organizationId?: string) =>
  createMutationFn<OrganizationSettings, UpdateOrganizationSettingsCategoryRequest>(
    organizationId
      ? `/api/settings/organization/category?organizationId=${organizationId}`
      : '/api/settings/organization/category',
    'PUT'
  );

// Delete/reset settings
export const deleteOrganizationSettings = (organizationId?: string) =>
  createMutationFn<{ message: string }, void>(
    organizationId
      ? `/api/settings/organization?organizationId=${organizationId}`
      : '/api/settings/organization',
    'DELETE'
  );

// Convenience functions for specific categories
export const updateVelocitySettings = (settings: VelocitySettings, organizationId?: string) =>
  updateOrganizationSettingsCategory(organizationId)({ category: 'velocitySettings', settings });

export const updateRepositorySettings = (settings: RepositorySettings, organizationId?: string) =>
  updateOrganizationSettingsCategory(organizationId)({ category: 'repositorySettings', settings });

export const updateAISettings = (settings: AISettings, organizationId?: string) =>
  updateOrganizationSettingsCategory(organizationId)({ category: 'aiSettings', settings });

export const updateAccessControlSettings = (settings: AccessControlSettings, organizationId?: string) =>
  updateOrganizationSettingsCategory(organizationId)({ category: 'accessControlSettings', settings });

export const updateOrgIntegrationSettings = (settings: OrganizationIntegrationSettings, organizationId?: string) =>
  updateOrganizationSettingsCategory(organizationId)({ category: 'integrationSettings', settings });

/**
 * Update DSA organization settings
 * DSA settings control whether DSA is enabled for the organization and how users participate
 */
export const updateDSAOrgSettings = async (dsaSettings: DSAOrgSettings, organizationId?: string) => {
  // DSA settings are stored under integrationSettings.dsa
  // We need to fetch current settings to merge with
  const currentSettings = await (await fetch(
    organizationId 
      ? `/api/settings/organization?organizationId=${organizationId}` 
      : '/api/settings/organization'
  )).json();
  
  const currentIntegration = currentSettings?.data?.integrationSettings || {};
  const updatedIntegration: OrganizationIntegrationSettings = {
    ...currentIntegration,
    dsa: dsaSettings,
  };
  
  return updateOrganizationSettingsCategory(organizationId)({ 
    category: 'integrationSettings', 
    settings: updatedIntegration 
  });
};

// Sync org settings to platform settings
export const syncOrgSettingsToPlatform = () =>
  createMutationFn<{ synced: string[]; message: string }, void>(
    '/api/settings/org-platform/sync',
    'POST'
  );

// Get sync status
export const getOrgPlatformSyncStatus = () =>
  createQueryOptions<{
    vertexAnthropic: {
      platform: { enabled: boolean; projectId: string; region: string };
      organization: { enabled: boolean; projectId: string; region: string } | null;
      synced: boolean;
    };
  }>('/api/settings/org-platform/status');
